import { useState, useEffect } from "react";
import {
  Label,
  Skeleton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Command,
  Button,
} from "@pushpress/pushpress-ui";
import { CheckIcon, XIcon, Check, Pencil } from "lucide-react";
import { EMPTY_VALUE } from "../../constants";
import { cn } from "../../../../utils/css";
import type { AssignedToOptionType } from "../../types";

type EditableAssignedToSelectFieldProps = {
  label: string;
  value?: string | null;
  onSave: (value: string) => void;
  hasData?: boolean;
  options: AssignedToOptionType[];
};

export default function EditableAssignedToSelectField({
  label,
  value = "",
  onSave,
  hasData = false,
  options,
}: EditableAssignedToSelectFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [open, setOpen] = useState(false);

  const selectedOption =
    options.find((option) => option.userId === Number(value))?.label ||
    EMPTY_VALUE;

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  if (!hasData) {
    return (
      <div className="tw-group">
        <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
        <Skeleton className="tw-h-4 tw-w-1/2" />
      </div>
    );
  }

  return (
    <div className="tw-group tw-h-12">
      <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
      {isEditing ? (
        <div className="tw-flex tw-items-center">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="tw-w-[330px] tw-justify-between"
              >
                {editValue
                  ? options.find(
                      (option) => option.userId === Number(editValue),
                    )?.label
                  : "Select staff member"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="tw-w-[330px] tw-p-0">
              <Command>
                <CommandInput
                  placeholder="Search by name or email"
                  className="tw-h-9"
                />
                <CommandList>
                  <CommandEmpty>No option found.</CommandEmpty>
                  <CommandGroup>
                    {options.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        keywords={[option.label, option.email]}
                        onSelect={(currentValue) => {
                          setEditValue(
                            currentValue === editValue ? "" : currentValue,
                          );
                          setOpen(false);
                        }}
                      >
                        {option.label}
                        <Check
                          className={cn(
                            "tw-ml-auto",
                            editValue === option.value
                              ? "tw-opacity-100"
                              : "tw-opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7"
            onClick={handleSave}
          >
            <CheckIcon className="tw-h-4 tw-w-4 tw-text-success" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7"
            onClick={handleCancel}
          >
            <XIcon className="tw-h-4 tw-w-4 tw-text-error" />
          </Button>
        </div>
      ) : (
        <div
          className="tw-cursor-pointer tw-py-1 hover:tw-bg-neutral-100 tw-transition-colors tw-group tw-flex tw-items-center"
          onClick={() => setIsEditing(true)}
          onKeyDown={() => setIsEditing(true)}
          role="button"
          tabIndex={0}
        >
          <p className="tw-font-medium group-hover:tw-text-primary tw-transition-colors">
            {selectedOption}
          </p>
          <div className="tw-ml-2 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
            <Pencil className="tw-h-4 tw-w-4 tw-text-neutral-600" />
          </div>
        </div>
      )}
    </div>
  );
}
